import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdSearch, MdBuild, MdEdit, MdDelete, MdVisibility, MdClose, MdChat, MdCheckCircle, MdWarning, MdInventory } from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';

const STATUS_OPTIONS = ['open', 'assigned', 'in_progress', 'on_hold', 'completed', 'cancelled'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'critical'];
const TYPE_OPTIONS = ['corrective', 'preventive', 'inspection', 'installation', 'other'];

async function copyText(text) {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch { /* Use the legacy browser fallback below. */ }
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (copied) return true;
    window.prompt(translateUiText('Copy this vendor link:'), text);
    return false;
}

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const emptyForm = { title: '', description: '', type: 'corrective', priority: 'medium', status: 'open', building_id: '', space_id: '', asset_id: '', assigned_to: '', due_date: '', estimated_hours: '', notes: '' };

export default function WorkOrders() {
    const { isAdmin, isManager, assignedFacility, facilityId } = useAuth();
    const { t } = useLanguage();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [assets, setAssets] = useState([]);
    const [comment, setComment] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    const [assetRequests, setAssetRequests] = useState([]);
    const [selectedQuotations, setSelectedQuotations] = useState({});

    // Parts needed for the work order currently open in the Detail modal —
    // "where one needs to be used, be ready": the readiness badge (green
    // "Ready" / red "N short") is computed live from actual inventory stock,
    // and completing the job auto-deducts these from stock server-side.
    const [inventoryItems, setInventoryItems] = useState([]);
    const [newPart, setNewPart] = useState({ item_id: '', quantity_needed: '' });
    const [partsBusy, setPartsBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (filterStatus) params.status = filterStatus;
            if (filterPriority) params.priority = filterPriority;
            const { data } = await API.get('/work-orders', { params });
            setOrders(data);
        } finally { setLoading(false); }
    }, [search, filterStatus, filterPriority]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        API.get('/auth/users/list').then(r => setUsers(r.data.filter(u => u.role !== 'developer')));
        API.get('/spaces/buildings').then(r => setBuildings(r.data));
        API.get('/assets').then(r => setAssets(r.data));
        API.get('/inventory').then(r => setInventoryItems(r.data));
    }, []);

    const openCreate = () => {
        const defaultForm = { ...emptyForm };
        if (isManager && facilityId) defaultForm.building_id = String(facilityId);
        setForm(defaultForm); setEditId(null); setShowModal(true);
    };
    const openEdit = wo => { setForm({ ...emptyForm, ...wo }); setEditId(wo.id); setShowModal(true); };

    // Completing a work order can be blocked server-side (409) if the parts
    // it needs aren't fully in stock. When that happens we show the shortage
    // and, on confirmation, resubmit the same payload with confirm_shortage
    // so the manager can proceed anyway (partial stock gets deducted, the
    // rest is just short — nothing is silently skipped).
    const saveWorkOrderPayload = async payload => {
        try {
            if (editId) await API.put(`/work-orders/${editId}`, payload);
            else await API.post('/work-orders', payload);
        } catch (err) {
            const shortage = err.response?.data?.shortage;
            if (err.response?.status === 409 && shortage) {
                const lines = shortage.map(s => `• ${s.item_name}: need ${s.quantity_needed}, have ${s.available}`).join('\n');
                if (window.confirm(`${err.response.data.error}\n\n${lines}\n\nComplete anyway?`)) {
                    await saveWorkOrderPayload({ ...payload, confirm_shortage: true });
                    return;
                }
            }
            throw err;
        }
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        try {
            await saveWorkOrderPayload(form);
            setShowModal(false);
            window.dispatchEvent(new Event('fms:data-changed'));
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Save failed'));
        } finally { setSaving(false); }
    };

    const openDetail = async wo => {
        const [detail, requests] = await Promise.all([
            API.get(`/work-orders/${wo.id}`), API.get('/asset-requests', { params: { work_order_id: wo.id } })
        ]);
        setShowDetail(detail.data);
        setAssetRequests(requests.data);
    };

    const refreshDetail = async () => {
        const { data } = await API.get(`/work-orders/${showDetail.id}`);
        setShowDetail(data);
        load();
    };

    const addPart = async () => {
        if (!newPart.item_id || !newPart.quantity_needed) return;
        setPartsBusy(true);
        try {
            await API.post('/inventory/requirements', {
                work_order_id: showDetail.id, item_id: newPart.item_id, quantity_needed: newPart.quantity_needed
            });
            setNewPart({ item_id: '', quantity_needed: '' });
            await refreshDetail();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not add part')); }
        finally { setPartsBusy(false); }
    };

    const removePart = async reqId => {
        setPartsBusy(true);
        try {
            await API.delete(`/inventory/requirements/${reqId}`);
            await refreshDetail();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not remove part')); }
        finally { setPartsBusy(false); }
    };

    const reviewAssetRequest = async (requestId, action) => {
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/${action}`);
            if (data.vendor_link) {
                const link = new URL(data.vendor_link, window.location.origin).href;
                try {
                    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(link)}`;
                    const qrBlob = await fetch(qrUrl).then(response => {
                        if (!response.ok) throw new Error('QR image unavailable');
                        return response.blob();
                    });
                    if (navigator.clipboard?.write && window.ClipboardItem) {
                        await navigator.clipboard.write([
                            new ClipboardItem({ 'image/png': qrBlob, 'text/plain': new Blob([link], { type: 'text/plain' }) })
                        ]);
                        alert(`${translateUiText('Request approved. Vendor link and QR code copied:')}\n${link}`);
                    } else {
                        const copied = await copyText(link);
                        alert(copied ? `${translateUiText('Request approved. Vendor link copied:')}\n${link}` : `${translateUiText('Select and copy the vendor link:')}\n${link}`);
                    }
                } catch {
                    const copied = await copyText(link);
                    alert(copied ? `${translateUiText('Request approved. Vendor link copied:')}\n${link}` : `${translateUiText('Select and copy the vendor link:')}\n${link}`);
                }
            }
            setAssetRequests((await API.get('/asset-requests', { params: { work_order_id: showDetail.id } })).data);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Request review failed')); }
    };

    const copyVendorLink = async requestId => {
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/vendor-link`);
            const link = new URL(data.vendor_link, window.location.origin).href;
            const copied = await copyText(link);
            alert(copied ? `${translateUiText('Vendor link copied:')}\n${link}` : `${translateUiText('Select and copy the vendor link:')}\n${link}`);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Could not create vendor link')); }
    };

    const reviewQuotation = async (requestId, action) => {
        const quotationId = selectedQuotations[requestId];
        if (!quotationId) {
            alert(translateUiText('Select a vendor quotation before approval.'));
            return;
        }
        try {
            const { data } = await API.post(`/asset-requests/${requestId}/quotation/${action}`, { quotation_id: quotationId });
            if (data.purchase_order) alert(`${translateUiText('Quotation approved. Purchase order')} ${data.purchase_order} ${translateUiText('created.')}`);
            if (data.purchase_order && data.vendor_email && !data.vendor_email.sent) alert(`The vendor email was NOT sent: ${data.vendor_email.error || 'unknown error'}. Check Settings > Email, or send the purchase order to the vendor yourself.`);
            setAssetRequests((await API.get('/asset-requests', { params: { work_order_id: showDetail.id } })).data);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Quotation review failed')); }
    };

    const handleDelete = async wo => {
        if (!window.confirm(`${translateUiText('Permanently delete')} ${wo.wo_number}? ${translateUiText('Its activity history will also be deleted. This cannot be undone.')}`)) return;
        try {
            await API.delete(`/work-orders/${wo.id}`);
            load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Delete failed')); }
    };

    const handleAddComment = async () => {
        if (!comment.trim()) return;
        setAddingComment(true);
        try {
            await API.post(`/work-orders/${showDetail.id}/updates`, { comment });
            setComment('');
            const { data } = await API.get(`/work-orders/${showDetail.id}`);
            setShowDetail(data);
        } finally { setAddingComment(false); }
    };

    const statusLabels = {
        open: t('workOrders.statusOpen', 'Open'), assigned: t('workOrders.statusAssigned', 'Assigned'),
        in_progress: t('workOrders.statusInProgress', 'In Progress'), on_hold: t('workOrders.statusOnHold', 'On Hold'),
        completed: t('workOrders.statusCompleted', 'Completed'), cancelled: t('workOrders.statusCancelled', 'Cancelled'),
    };
    const priorityLabels = {
        low: t('workOrders.priorityLow', 'Low'), medium: t('workOrders.priorityMedium', 'Medium'),
        high: t('workOrders.priorityHigh', 'High'), critical: t('workOrders.priorityCritical', 'Critical'),
    };
    const typeLabels = {
        corrective: t('workOrders.typeCorrective', 'corrective'), preventive: t('workOrders.typePreventive', 'preventive'),
        inspection: t('workOrders.typeInspection', 'inspection'), installation: t('workOrders.typeInstallation', 'installation'),
        other: t('workOrders.typeOther', 'other'),
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('workOrders.pageTitle', 'Work Orders')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {isManager && assignedFacility
                            ? `${assignedFacility.name} ${t('workOrders.pageSubtitle', '— service requests and tasks')}`
                            : t('workOrders.pageSubtitleShort', 'Manage service requests and tasks')}
                    </p>
                </div>
                <button className="btn btn-primary" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MdAdd size={18} /> {t('workOrders.newButton', 'Work Order')}
                </button>
            </div>

            {/* Filters */}
            <div className="page-card mb-4">
                <div style={{ padding: '14px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                        <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input className="form-control" style={{ paddingLeft: 34 }} placeholder={t('workOrders.searchPlaceholder', 'Search work orders...')}
                            value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="">{t('workOrders.allStatus', 'All Status')}</option>
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                    </select>
                    <select className="form-select" style={{ width: 140 }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                        <option value="">{t('workOrders.allPriority', 'All Priority')}</option>
                        {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{priorityLabels[p]}</option>)}
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? (
                        <div className="spinner-wrap"><div className="spinner-border" /></div>
                    ) : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>{t('workOrders.colWoNumber', 'WO #')}</th><th>{t('workOrders.colTitle', 'Title')}</th><th>{t('workOrders.colType', 'Type')}</th><th>{t('workOrders.colPriority', 'Priority')}</th>
                                    <th>{t('workOrders.colStatus', 'Status')}</th><th>{t('workOrders.colAssignedTo', 'Assigned To')}</th><th>{t('workOrders.colDueDate', 'Due Date')}</th><th>{t('workOrders.colParts', 'Parts')}</th><th>{t('workOrders.colActions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(wo => (
                                    <tr key={wo.id}>
                                        <td><span style={{ fontFamily: 'monospace', fontSize: 12, color: '#0ea5e9', fontWeight: 600 }}>{wo.wo_number}</span></td>
                                        <td style={{ maxWidth: 220 }}>
                                            <div style={{ fontWeight: 500 }}>{wo.title}</div>
                                            {wo.building_name && <div style={{ fontSize: 11, color: '#94a3b8' }}>{wo.building_name}</div>}
                                        </td>
                                        <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{wo.type?.replace('_', ' ')}</td>
                                        <td><span className={`badge-priority ${wo.priority}`}>{wo.priority}</span></td>
                                        <td><span className={`badge-status ${wo.status}`}>{wo.status?.replace('_', ' ')}</span></td>
                                        <td style={{ fontSize: 13 }}>{wo.assignee_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                        <td style={{ fontSize: 12 }}>{wo.due_date || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                                        <td>
                                            {wo.parts_total > 0 ? (
                                                wo.parts_ready ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d' }}>
                                                        <MdCheckCircle size={12} /> {t('workOrders.partsReady', 'Ready')}
                                                    </span>
                                                ) : (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#fee2e2', color: '#b91c1c' }}>
                                                        <MdWarning size={12} /> {t('workOrders.partsShort', 'Short')}
                                                    </span>
                                                )
                                            ) : <span style={{ color: '#cbd5e1', fontSize: 12 }}>—</span>}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="btn btn-sm btn-light" onClick={() => openDetail(wo)} title={t('workOrders.view', 'View')}><MdVisibility size={15} /></button>
                                                <button className="btn btn-sm btn-light" onClick={() => openEdit(wo)} title={t('workOrders.edit', 'Edit')}><MdEdit size={15} /></button>
                                                <button className="btn btn-sm btn-light" style={{ color: '#ef4444' }} onClick={() => handleDelete(wo)} title={t('workOrders.delete', 'Delete')}><MdDelete size={15} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!orders.length && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                                        <MdBuild size={32} style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px' }} />
                                        {t('workOrders.noneFound', 'No work orders found')}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <Modal title={editId ? t('workOrders.editModalTitle', 'Edit Work Order') : t('workOrders.createModalTitle', 'Work Order')} onClose={() => setShowModal(false)}>
                    <form onSubmit={handleSave}>
                        <div className="row g-3">
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('workOrders.labelTitle', 'Title *')}</label>
                                <input className="form-control" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('workOrders.labelDescription', 'Description')}</label>
                                <textarea className="form-control" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelType', 'Type')}</label>
                                <select className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                    {TYPE_OPTIONS.map(ty => <option key={ty} value={ty}>{typeLabels[ty]}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelPriority', 'Priority')}</label>
                                <select className="form-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                                    {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{priorityLabels[p]}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelStatus', 'Status')}</label>
                                <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{statusLabels[s]}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelAssignedTo', 'Assigned To')}</label>
                                <select className="form-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                                    <option value="">{t('workOrders.unassigned', 'Unassigned')}</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.name}{u.job_title ? ` (${u.job_title})` : u.role !== 'employee' ? ` (${u.role})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelBuilding', 'Building')}</label>
                                {isManager && assignedFacility ? (
                                    <input className="form-control" value={assignedFacility.name} readOnly
                                        style={{ background: '#f8fafc', color: '#64748b' }} />
                                ) : (
                                    <select className="form-select" value={form.building_id} onChange={e => setForm({ ...form, building_id: e.target.value })}>
                                        <option value="">{t('workOrders.selectBuilding', 'Select building')}</option>
                                        {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                )}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelAsset', 'Asset')}</label>
                                <select className="form-select" value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })}>
                                    <option value="">{t('workOrders.noAsset', 'No asset')}</option>
                                    {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelDueDate', 'Due Date')}</label>
                                <input type="date" className="form-control" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">{t('workOrders.labelEstimatedHours', 'Estimated Hours')}</label>
                                <input type="number" className="form-control" step="0.5" value={form.estimated_hours} onChange={e => setForm({ ...form, estimated_hours: e.target.value })} />
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">{t('workOrders.labelNotes', 'Notes')}</label>
                                <textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button type="button" className="btn btn-light" onClick={() => setShowModal(false)}>{t('workOrders.cancel', 'Cancel')}</button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('workOrders.saving', 'Saving...')}</> : t('workOrders.saveButton', 'Save Work Order')}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Detail Modal */}
            {showDetail && (
                <Modal title={`${showDetail.wo_number} — ${showDetail.title}`} onClose={() => setShowDetail(null)}>
                    <div className="row g-2 mb-3" style={{ fontSize: 13 }}>
                        <div className="col-6"><strong>Status:</strong> <span className={`badge-status ${showDetail.status}`}>{showDetail.status?.replace('_', ' ')}</span></div>
                        <div className="col-6"><strong>Priority:</strong> <span className={`badge-priority ${showDetail.priority}`}>{showDetail.priority}</span></div>
                        <div className="col-6"><strong>Type:</strong> {showDetail.type}</div>
                        <div className="col-6"><strong>Assigned:</strong> {showDetail.assignee_name || '—'}</div>
                        <div className="col-6"><strong>Due Date:</strong> {showDetail.due_date || '—'}</div>
                        <div className="col-6"><strong>Building:</strong> {showDetail.building_name || '—'}</div>
                        {showDetail.description && <div className="col-12"><strong>Description:</strong> {showDetail.description}</div>}
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14, marginBottom: 16 }}>
                        <h6 style={{ fontSize: 13 }}><MdInventory style={{ marginRight: 6 }} />{t('workOrders.partsNeededLabel', 'Parts Needed ({n})', { n: showDetail.parts_needed?.length || 0 })}</h6>
                        {showDetail.parts_needed?.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#f8fafc', padding: '8px 10px', marginBottom: 6, borderRadius: 6, fontSize: 12 }}>
                                <div>
                                    <strong>{p.item_name}</strong> <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{p.item_code}</span>
                                    <div style={{ color: '#64748b' }}>{t('workOrders.needHaveLabel', 'Need {q}{unit} · Have {avail}{unit}', { q: p.quantity_needed, unit: p.unit ? ` ${p.unit}` : '', avail: p.available })}</div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {p.fulfilled ? (
                                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#e0f2fe', color: '#0369a1' }}>{t('workOrders.partDeducted', 'Deducted')}</span>
                                    ) : p.ready ? (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#dcfce7', color: '#15803d' }}><MdCheckCircle size={12} /> {t('workOrders.partsReady', 'Ready')}</span>
                                    ) : (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: '#fee2e2', color: '#b91c1c' }}><MdWarning size={12} /> {t('workOrders.partsShort', 'Short')}</span>
                                    )}
                                    {!p.fulfilled && (isAdmin || isManager) && (
                                        <button type="button" className="btn btn-sm btn-light" disabled={partsBusy} onClick={() => removePart(p.id)} title={t('workOrders.removeFromPlan', 'Remove from plan')}><MdClose size={13} /></button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {!showDetail.parts_needed?.length && <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>{t('workOrders.noPartsPlanned', 'No parts planned for this job yet.')}</div>}
                        {(isAdmin || isManager) && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                <select className="form-select form-select-sm" style={{ flex: 1 }} value={newPart.item_id} onChange={e => setNewPart({ ...newPart, item_id: e.target.value })}>
                                    <option value="">{t('workOrders.selectPart', 'Select a part...')}</option>
                                    {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name} ({i.item_code}) — {i.quantity_in_stock}{i.unit ? ` ${i.unit}` : ''} in stock</option>)}
                                </select>
                                <input type="number" className="form-control form-control-sm" style={{ width: 90 }} min={0.01} step="0.01" placeholder={t('workOrders.qtyPlaceholder', 'Qty')} value={newPart.quantity_needed} onChange={e => setNewPart({ ...newPart, quantity_needed: e.target.value })} />
                                <button type="button" className="btn btn-sm btn-outline-primary" disabled={partsBusy || !newPart.item_id || !newPart.quantity_needed} onClick={addPart}>{t('workOrders.addPartButton', 'Add')}</button>
                            </div>
                        )}
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{t('workOrders.completeDeductHint', "Marking this work order Completed auto-deducts these from stock — it's blocked if stock is short unless you confirm.")}</div>
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14, marginBottom: 16 }}>
                        <h6 style={{ fontSize: 13 }}>{t('workOrders.assetRequestsLabel', 'Asset Requests ({n})', { n: assetRequests.length })}</h6>
                        {assetRequests.map(assetRequest => <div key={assetRequest.id} style={{ background: '#f8fafc', padding: 10, marginBottom: 8, fontSize: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <strong>{assetRequest.request_type === 'inventory_item' ? '📦 ' : '🆕 '}{assetRequest.asset_name} × {assetRequest.quantity}</strong>
                                <span className={`badge-status ${assetRequest.status}`}>{assetRequest.status.replaceAll('_', ' ')}</span>
                            </div>
                            <div style={{ color: '#64748b' }}>{t('workOrders.requestedBy', 'Requested by {name} ·', { name: assetRequest.requester_name || 'user' })} {assetRequest.request_type === 'inventory_item' ? t('workOrders.restockItem', 'Restock inventory item') : t('workOrders.newAsset', 'New asset')}</div>
                            {(assetRequest.fulfilled_asset_id || assetRequest.fulfilled_requirement_id) && (
                                <div style={{ color: '#15803d', fontWeight: 600, marginTop: 2 }}>
                                    ✓ {t('workOrders.autoAddedAfterPO', 'Auto-added to this job after PO approval')}
                                </div>
                            )}
                            {assetRequest.justification && <div>{assetRequest.justification}</div>}
                            {assetRequest.quotation_amount != null && <div style={{ marginTop: 4 }}><strong>Vendor:</strong> {assetRequest.vendor_name} ({assetRequest.vendor_email}) · <strong>Quotation:</strong> {assetRequest.quotation_currency} {assetRequest.quotation_amount} · Delivery: {assetRequest.delivery_time}{assetRequest.quotation_notes ? ` · ${assetRequest.quotation_notes}` : ''}</div>}
                            {assetRequest.vendor_quotations?.length > 0 && <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 6 }}>
                                <strong>{t('workOrders.vendorSubmissions', 'Vendor submissions ({n})', { n: assetRequest.vendor_quotations.length })}</strong>
                                {assetRequest.vendor_quotations.map(quotation => <label key={quotation.id} style={{ display: 'block', marginTop: 5, padding: 6, background: '#fff', border: `1px solid ${selectedQuotations[assetRequest.id] === quotation.id ? '#0d6efd' : '#e2e8f0'}`, cursor: quotation.status === 'submitted' ? 'pointer' : 'default' }}>
                                    <input type="radio" name={`quotation-${assetRequest.id}`} className="me-2" value={quotation.id} checked={selectedQuotations[assetRequest.id] === quotation.id} disabled={quotation.status !== 'submitted'} onChange={() => setSelectedQuotations(current => ({ ...current, [assetRequest.id]: quotation.id }))} />
                                    <strong>{quotation.vendor_name}</strong> ({quotation.vendor_email}) · {quotation.currency} {quotation.amount} · {quotation.delivery_time}
                                    <span className={`badge-status ${quotation.status}`} style={{ marginLeft: 6 }}>{quotation.status}</span>
                                    {quotation.notes && <div>{quotation.notes}</div>}
                                </label>)}
                            </div>}
                            {(isAdmin || isManager) && <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                {assetRequest.status === 'pending' && <><button className="btn btn-sm btn-primary" onClick={() => reviewAssetRequest(assetRequest.id, 'approve')}>{t('workOrders.approveCreateLink', 'Approve & create vendor link')}</button><button className="btn btn-sm btn-light" onClick={() => reviewAssetRequest(assetRequest.id, 'reject')}>{t('workOrders.reject', 'Reject')}</button></>}
                                {['approved', 'quotation_submitted', 'quotation_approved', 'quotation_rejected'].includes(assetRequest.status) && <button className="btn btn-sm btn-outline-primary" onClick={() => copyVendorLink(assetRequest.id)}>{t('workOrders.copyVendorLink', 'Copy vendor link')}</button>}
                                {assetRequest.status === 'quotation_submitted' && <><button className="btn btn-sm btn-primary" onClick={() => reviewQuotation(assetRequest.id, 'approve')}>{t('workOrders.approveQuotation', 'Approve selected quotation')}</button><button className="btn btn-sm btn-light" onClick={() => reviewQuotation(assetRequest.id, 'reject')}>{t('workOrders.rejectQuotation', 'Reject selected quotation')}</button></>}
                            </div>}
                        </div>)}
                        {!assetRequests.length && <div style={{ color: '#94a3b8', fontSize: 12 }}>{t('workOrders.noAssetRequests', 'No asset requests for this work order.')}</div>}
                    </div>

                    <hr />
                    <h6 style={{ fontSize: 13 }}><MdChat style={{ marginRight: 6 }} />{t('workOrders.activityLogLabel', 'Activity Log ({n})', { n: showDetail.updates?.length || 0 })}</h6>
                    <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12 }}>
                        {showDetail.updates?.map(u => (
                            <div key={u.id} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600, fontSize: 12, color: '#0ea5e9' }}>{u.user_name}</span>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(u.created_at).toLocaleString()}</span>
                                </div>
                                <div style={{ fontSize: 13 }}>{u.comment}</div>
                            </div>
                        ))}
                        {!showDetail.updates?.length && <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('workOrders.noUpdates', 'No updates yet.')}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input className="form-control form-control-sm" placeholder={t('workOrders.commentPlaceholder', 'Add a comment...')} value={comment}
                            onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAddComment()} />
                        <button className="btn btn-primary btn-sm" onClick={handleAddComment} disabled={addingComment}>{t('workOrders.postButton', 'Post')}</button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
